import {
  ArrowRight,
  Camera,
  Clock,
  HelpCircle,
  Lightbulb,
  LogOut,
  MessageSquare,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface HowItWorksProps {
  onStart: () => void;
  onExit: () => void;
}

export function HowItWorks({ onStart, onExit }: HowItWorksProps) {
  const { t } = useTranslation();

  const infoCards = [
    {
      icon: MessageSquare,
      titleKey: 'video.howItWorks.prompts.title',
      descKey: 'video.howItWorks.prompts.desc',
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      icon: Clock,
      titleKey: 'video.howItWorks.timeLimit.title',
      descKey: 'video.howItWorks.timeLimit.desc',
      color: 'text-amber-600',
      bg: 'bg-amber-50',
    },
    {
      icon: RefreshCw,
      titleKey: 'video.howItWorks.retakePolicy.title',
      descKey: 'video.howItWorks.retakePolicy.desc',
      color: 'text-green-600',
      bg: 'bg-green-50',
    },
    {
      icon: ShieldCheck,
      titleKey: 'video.howItWorks.privacy.title',
      descKey: 'video.howItWorks.privacy.desc',
      color: 'text-purple-600',
      bg: 'bg-purple-50',
    },
  ];

  const tips = [
    { key: 'video.howItWorks.tips.tip1', icon: Lightbulb },
    { key: 'video.howItWorks.tips.tip2', icon: Camera },
    { key: 'video.howItWorks.tips.tip3', icon: HelpCircle },
    { key: 'video.howItWorks.tips.tip4', icon: HelpCircle },
    { key: 'video.howItWorks.tips.tip5', icon: HelpCircle },
  ];

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div>
        <h2 className='text-2xl font-bold text-gray-900'>{t('video.howItWorks.title')}</h2>
        <p className='text-gray-500 mt-1'>{t('video.howItWorks.subtitle')}</p>
      </div>

      {/* Info cards grid */}
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
        {infoCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.titleKey} className='border border-gray-200 rounded-lg p-4'>
              <div className='flex items-start gap-3'>
                <div className={`p-2 rounded-lg ${card.bg} shrink-0`}>
                  <Icon size={20} className={card.color} />
                </div>
                <div>
                  <h3 className='font-semibold text-gray-900'>{t(card.titleKey)}</h3>
                  <p className='text-sm text-gray-500 mt-1'>{t(card.descKey)}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tips section */}
      <div className='bg-gray-50 rounded-lg p-5'>
        <h3 className='font-semibold text-gray-900 mb-3'>{t('video.howItWorks.tips.title')}</h3>
        <ul className='space-y-2'>
          {tips.map((tip) => {
            const TipIcon = tip.icon;
            return (
              <li key={tip.key} className='flex items-start gap-2 text-sm text-gray-600'>
                <TipIcon size={16} className='text-gray-400 mt-0.5 shrink-0' />
                <span>{t(tip.key)}</span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Actions */}
      <div className='flex flex-col sm:flex-row items-center justify-between gap-3 pt-2'>
        <button
          type='button'
          onClick={onExit}
          className='flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors'>
          <LogOut size={16} className='mr-1' />
          {t('video.howItWorks.exit')}
        </button>
        <button
          type='button'
          onClick={onStart}
          className='flex items-center justify-center px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition w-full sm:w-auto'>
          {t('video.howItWorks.startRecording')}
          <ArrowRight size={18} className='ml-2' />
        </button>
      </div>
    </div>
  );
}
